import dayjs from 'dayjs'
import { Game, Round } from '@/redux/reducers/gameReducer'
import {
  isEncryptLotto,
  isGroupLotto,
  isYeekeeType,
} from '@/utils/lotto-type-v2'
import { YeekeeLottoResultCard } from './YeekeeLottoResultCard'
import { ThaiLottoResultCard } from './ThaiLottoResultCard'
import { LottoCountryResultCard } from './LottoCountryResultCard'
import gamePageStyles from '@/ui-components/pages/GamePage.module.css'

interface ResultCardProps {
  game: Game
  rounds?: Round[]
  isLottoRoundPage?: boolean
}

/**
 * ResultCard - A component to display the result card for different lottery types
 * @param ResultCardProps - The game object containing lottery information
 * @returns The appropriate result card component based on the lottery type
 *
 * This component creates different types of result cards based on lottery types:
 * - YeekeeLottoCard: For Yeekee and Encrypt lotto types
 * - ThaiLottoCard: For Thai Government and Group lotto types
 * - LottoCountryCard: For all other standard lotto types
 */
const ResultCard = ({ game, rounds, isLottoRoundPage }: ResultCardProps) => {
  const gameRounds = rounds || game.rounds

  // Handle Yeekee type games and Encrypt Lotto games
  if (isYeekeeType(game.type) || isEncryptLotto(game.type)) {
    let roundsData: Round[] = []

    if (isLottoRoundPage) {
      // If on the lotto round page, we want to show the last 10 closed rounds
      const now = dayjs()

      const closedRound = [...gameRounds]
        .filter((round) => dayjs(round.close_at).isBefore(now))
        .sort((a, b) => dayjs(b.close_at).diff(dayjs(a.close_at))) // Sort open rounds in descending order (from newest to oldest)

      roundsData = closedRound
        .filter((i) => dayjs(i.close_at).isSame(dayjs(), 'date'))
        .slice(0, 10)
    } else {
      const lastFiveRounds =
        gameRounds.length > 0 ? [...gameRounds].slice(-5) : []
      roundsData = lastFiveRounds
    }

    const yeekeeCard = (
      <YeekeeLottoResultCard
        key={`yeekee-result-${game.id}`}
        game={game}
        rounds={roundsData}
      />
    )

    // Wrap with bannerWrapper on rounds page (not result page)
    if (isLottoRoundPage) {
      return (
        <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
          {yeekeeCard}
        </div>
      )
    }

    return yeekeeCard
  }

  // Handle Thai Government Lotto (which uses ThaiLottoCard)
  if (game.flag === 'government' || isGroupLotto(game.type)) {
    const thaiCard = (
      <ThaiLottoResultCard
        key={`thai-result-${game.id}`}
        game={game}
        rounds={gameRounds}
      />
    )

    // Wrap with bannerWrapper on rounds page (not result page)
    if (isLottoRoundPage) {
      return (
        <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
          {thaiCard}
        </div>
      )
    }

    return thaiCard
  }

  if (isGroupLotto(game.type)) {
    return <div key={`group-${game.id}`}>TODO: wait group table</div>
  }

  // Handle normal lotto games (LottoCountryCard)
  const countryCard = (
    <LottoCountryResultCard
      key={`country-result-${game.id}`}
      game={game}
      rounds={gameRounds}
    />
  )

  // Wrap with bannerWrapper on rounds page (not result page)
  if (isLottoRoundPage) {
    return (
      <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
        {countryCard}
      </div>
    )
  }

  return countryCard
}

export default ResultCard
